/**
 * @file Polls for a new deployed platform version and shows a banner
 * that counts down to an automatic page refresh.
 */
import { useState, useEffect, useCallback } from "react";
import { Alert } from "antd";

import { Markdown } from "@components/Text";
import { isLocal } from "@components/Constants";
import { getConfig } from "@components/Config";

const LABEL_MESSAGE = `New application version [VERSION] released, refresh the
page in [SECONDS] seconds...`;

const PLATFORM_VERSION = getConfig<string>("PLATFORM_VERSION");

const PATH = "/static/release.json";
const UPDATE_DELAY = 15 * 1000;
const REFRESH_STEP = 1 * 1000;
const REFRESH_DELAY = 5;

type ReleaseJson = {
  version: string;
};

/**
 * Renders a banner with a countdown to refresh once a newer platform
 * version is deployed than the one the page loaded with.
 */
const ReleaseAlert = (): JSX.Element | null => {
  const [ seconds, setSeconds ] = useState(REFRESH_DELAY);
  const [ version, setVersion ] = useState(PLATFORM_VERSION);

  const autoRefresh = useCallback(
    (currentVersion: string) => {
      let secondsLeft = seconds;

      const countdown = setInterval(() => {
        secondsLeft -= 1;

        const shouldRefresh = secondsLeft === 0;

        if (shouldRefresh) {
          clearInterval(countdown);

          const [ url ] = window.location.href.split("?");
          window.location.href = `${url}?v=${currentVersion}`;

          return;
        }

        setSeconds(secondsLeft);
      }, REFRESH_STEP);
    }
  , [ seconds ]);

  const updateVersion = useCallback(
    async () => {
      const timestamp = new Date().getTime();

      const url = `${PATH}?ts=${timestamp}`;
      let result: ReleaseJson;

      try {
        const response = await fetch(url);
        result = await response.json();

      } catch (error) {
        console.error(error);

        return;
      }

      const { version: currentVersion } = result;

      setVersion(currentVersion);
      const shouldRefresh = currentVersion !== PLATFORM_VERSION;

      if (!shouldRefresh) {
        return;
      }

      if (isLocal) {
        return;
      }

      autoRefresh(currentVersion);
    }
  , [ autoRefresh ]);

  useEffect(() => {
    if (!isLocal) {
      updateVersion();
    }

    const interval = setInterval(updateVersion, UPDATE_DELAY);

    return () => clearInterval(interval);
  }, [ updateVersion ]);

  const isLatestVersion = version === PLATFORM_VERSION;

  if (isLatestVersion) {
    return null;
  }

  const text = LABEL_MESSAGE
    .replace("[VERSION]", version)
    .replace("[SECONDS]", String(seconds));

  return (
    <div className="header-alerts">
      <Alert
        banner
        message={
          <Markdown text={text} />
        }
      />
    </div>
  );
};

export default ReleaseAlert;
